import React, { useState, useEffect } from "react";
import { FontStyles } from "../../base/fonts/fontStyles";
import { PropertySearchContainer } from "./PropertySearch.style";
import { PropertySearchProps } from "./PropertySearch.types";
import ButtonCTA from "../ButtonCTA/ButtonCTA";
import { tagCtaClick } from "../../utils/tagManager";

declare const window: any;

const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

const getIsAuthor = () =>
  typeof window !== "undefined" &&
  (window.location.pathname.includes("/editor.html") ||
    window.location.pathname.includes("/content/conhecer/br/pt"));

const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

export default function PropertySearch({
  id,
  backgroundColor,
  title,
  titleSearch,
  textSearch,
  mcmvChecker,
  buttonSearch,
  iconCheck,
  isInEditor,
  cqPath,
  pageSearchUrl
}: PropertySearchProps) {
  return (
    <>
      <FontStyles />
      <PropertySearchContainer id={id} $backgroundcolor={backgroundColor}>
        <div className="property-search-container">
          <div className="property-search-content">
            <h2 style={!titleSearch ? { display: "none" } : {}}>{titleSearch}</h2>
            <p style={!textSearch ? { display: "none" } : {}}>{textSearch}</p>
            <div className="property-search-actions">
              <PropertySearchForm
              
                    title={title}
                    backgroundColor={backgroundColor}
                    isInEditor={isInEditor}
                    cqPath={cqPath}
                    titleSearch={titleSearch}
                    textSearch={textSearch}
                    mcmvChecker={mcmvChecker}
                    buttonSearch={buttonSearch}
                    iconCheck={iconCheck}
                    pageSearchUrl={pageSearchUrl}
                  />

            </div>
          </div>
        </div>
      </PropertySearchContainer>
    </>
  );
}

interface Estado {
  estado: string;
  estadoSlug: string;
  cidades: {
    nome: string;
    slug: string;
    bairro: {
      nome: string;
      slug: string;
    }[];
  }[];
}

const PropertySearchForm: React.FC<PropertySearchProps> = ({
  title,
  mcmvChecker,
  buttonSearch,
  iconCheck,
  pageSearchUrl
}) => {
  const [data, setData] = useState<Estado[]>([]);
  const [selectedEstado, setSelectedEstado] = useState<Estado | null>(null);
  const [selectedCidadeSlug, setSelectedCidadeSlug] = useState("");
  const [selectedBairroSlug, setSelectedBairroSlug] = useState("");
  
  const { width } = useScreenSize();
  const isMobile = width < 992;
  
  // Definir tamanhos máximos para placeholders baseado na resolução
  const getPlaceholderMaxLength = () => {
    if (width < 480) return 12; // Mobile muito pequeno
    if (width < 769) return 15; // Mobile
    if (width < 1100) return 15; // tablet
    return 20; // Desktop
  };

  const maxLength = getPlaceholderMaxLength();

  useEffect(() => {
    const fetchData = async () => {
      try {
        let url = "/bin/mrv/properties/state-cities/list";
        if (mcmvChecker) {
          url += "?classification=mcmv";
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error(`Erro ${response.status}: ${response.statusText}`);

        const result = await response.json();
        const formattedData = result.states.map((item: any) => ({         
          estado: item.label,
          estadoSlug: item.slug,
          cidades: item.cities.map((c: any) => ({
            nome: c.label,
            slug: c.slug,
            bairro: (c.neighborhoods || []).map((b: any) => ({
              nome: b.label,
              slug: b.slug,
            })),
          })),
        }));
        setData(formattedData);
      } catch (err) {
        console.error("Erro ao buscar estados/cidades:", err);
      }
    };

    fetchData();
  }, [mcmvChecker]);

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const slug = e.target.value;
    const estadoSelecionado = data.find((uf) => uf.estadoSlug === slug);
    setSelectedEstado(estadoSelecionado || null);
    setSelectedCidadeSlug("");
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCidadeSlug(e.target.value);
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!selectedEstado) return;

       // Dispara evento no dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "search",
      search_term: `${selectedBairroSlug || ""} - ${selectedCidadeSlug || ""} - ${selectedEstado.estado}`
    });

    tagCtaClick({
          element_text: "Botão de Busca",
          element_location: "header",
          content_id: "cta_search",
          section_title: "Header",
        })

    let baseUrl = pageSearchUrl;
    
    if (getIsAuthor() && pageSearchUrl && !pageSearchUrl.endsWith('.html')) {
       baseUrl = `${pageSearchUrl}.html`;
    } else if (!pageSearchUrl) {
        baseUrl = "/content/conhecer/br/pt/resultado-da-busca.html";
    }

    let url = `${baseUrl}?state=${selectedEstado.estadoSlug}`;
    if (selectedCidadeSlug) url += `&city=${selectedCidadeSlug}`;
    if (selectedBairroSlug) url += `&neighborhoods=${selectedBairroSlug}`;
    if (mcmvChecker) url += `&classification=mcmv`;

    window.location.href = url;

  };

  return (
    <form className="property-search-form">
      <span className="title-search">{title}</span>

      <div className="form-group">
        <label htmlFor="state-select" className="form-label">Estado</label>
        <select
          id="state-select"
          className="select-input"
          value={selectedEstado?.estadoSlug || ""}
          onChange={handleStateChange}
        >
          <option value="">Buscar por estado</option>
          {data.map((estado) => (
            <option key={estado.estadoSlug} value={estado.estadoSlug}>
              {estado.estado}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="city-select" className="form-label">Cidade</label>
        <select
          id="city-select"
          className="select-input"
          value={selectedCidadeSlug}
          onChange={handleCityChange}
          disabled={!selectedEstado}
        >
          <option value="">Buscar por cidade</option>
          {selectedEstado?.cidades.map((cidade) => (
            <option key={cidade.slug} value={cidade.slug}>
              {cidade.nome}
            </option>
          ))}
        </select>
      </div>
      {(selectedEstado?.cidades.find(
        (cidade) => cidade.slug === selectedCidadeSlug && 
        (cidade.nome === "Rio de Janeiro" || cidade.nome === "São Paulo")
      )) && (
        <div className="form-group">
          <label htmlFor="bairro-select" className="form-label">Bairro</label>
          <select
        id="bairro-select"
        className="select-input"
        value={selectedBairroSlug}
        onChange={e => setSelectedBairroSlug(e.target.value)}
        disabled={!selectedCidadeSlug}
          >
        <option value="">{truncateText("Buscar por região", maxLength)}</option>
        {(selectedEstado?.cidades.find((cidade) => cidade.slug === selectedCidadeSlug)?.bairro ?? []).map(
          (bairro: { nome: string; slug: string }) => (
            <option key={bairro.slug} value={bairro.slug}>
          {bairro.nome}
            </option>
          )
        )}
          </select>
        </div>
      )}

      <ButtonCTA
        asButton={true}
        disabled={!selectedEstado}
        label={buttonSearch}
        styleButton="defaultFullWidth"
        onClickAttribute={handleSubmit}
        className="custom-button"
        isInEditor={false}
        cqPath=""
        backgroundcolor="#FFF"
        iconCheck={iconCheck}
      />
    </form>
  );
};
